import { useParams, Link } from "react-router-dom";
import { categories, categoryMap } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import SEOHead from "@/components/feature/SEOHead";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : null;
  const categoryArticles = articles.filter((a) => category && a.categoryId === category.id);

  if (!category) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <h2 className="text-xl font-semibold text-foreground-950 mb-2">分类不存在</h2>
        <p className="text-sm text-foreground-500 mb-6">请检查网址或返回首页</p>
        <Link to="/" className="text-sm text-accent-500 hover:underline cursor-pointer">
          返回首页
        </Link>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `https://17cyqc.com/category/${slug}/#collection`,
        url: `https://17cyqc.com/category/${slug}`,
        name: `${category.name} - 17c一起草国`,
        description: category.description,
        isPartOf: { "@id": "https://17cyqc.com/#website" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://17cyqc.com" },
          { "@type": "ListItem", position: 2, name: category.name },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        meta={{
          title: `${category.name} - 17c一起草国`,
          description: `${category.description}，17c一起草国为您提供${category.name}相关的优质内容和完整版文章，每日更新精选推荐。`,
          keywords: `17c一起草国, ${category.name}, 免费阅读, 内容推荐, 完整版`,
          canonical: `https://17cyqc.com/category/${slug}`,
          ogTitle: `${category.name} - 17c一起草国`,
          ogDescription: category.description,
          ogUrl: `https://17cyqc.com/category/${slug}`,
          jsonLd,
        }}
      />

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
            {category.name}
          </h1>
          <p className="text-sm text-foreground-600">{category.description}</p>
        </div>

        {categoryArticles.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-foreground-500 text-sm">暂无内容，敬请期待</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-accent-200 transition-colors cursor-pointer"
                data-product-shop=""
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    title={`${article.title} - 17c一起草国`}
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-base font-semibold text-foreground-950 mb-2 group-hover:text-accent-500 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>{article.author}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" />
                      {article.viewCount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 pb-14">
        <div className="bg-background-100 rounded-lg p-6 flex flex-wrap items-center gap-3">
          <span className="text-sm text-foreground-600 font-medium whitespace-nowrap">其他分类：</span>
          {categories
            .filter((c) => c.slug !== slug)
            .map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-50 text-foreground-700 border border-background-200/70 hover:border-accent-200 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                {cat.name}
              </Link>
            ))}
        </div>
      </section>
    </>
  );
}